/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, memo, useRef } from 'react';
import '../CustomFiltersStyle.scss';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Input, Menu, MenuItem, Stack, styled, Tooltip, tooltipClasses, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { deprecatedCategoryList, METADATA_KEY } from '../../../../../utils/utils';
import { CssTextField } from '../../../../Pages/Events/styles';
import { sanitizeText, getFilterDataTestIdKey } from '../../../../../utils/helper';
import { QUICK_DATE_RANGE_OPTIONS } from '../../../../../utils/dateQuickRanges';
import CustomBytesFilter from './CustomBytesFilter';
import CustomConditionalFilter from './CustomConditionalFilter';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

function convertDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  // Use local date so the chip shows the date the user selected (avoids UTC off-by-one in timezones ahead of UTC)
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}


export const NoMaxWidthTooltip = styled(
  ({ className, toolTipWidth = "400px", disablePortal = false, backgroundColor = "#000", zoom = 1.25, zIndex = 10000, PopperProps: externalPopperProps = {}, pointerEvents = "all", ...props }) => (
    <Tooltip
      {...props}
      PopperProps={{
        disablePortal,
        ...externalPopperProps,
        style: { zIndex, ...externalPopperProps.style, pointerEvents },
      }}
      classes={{ popper: className }}
    />
  )
)(({ toolTipWidth = "400px", disablePortal = false, backgroundColor = "#000", zoom = 1.25, zIndex = 10000, pointerEvents = "all" }) => ({
  zIndex,
  ...(disablePortal && { zoom }),
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: toolTipWidth,
    padding: '0px',
    margin: '0px',
    backgroundColor: backgroundColor,
    color: '#fff',
    fontFamily: 'Montserrat',
    pointerEvents: pointerEvents,
  },
}));

export const CustomTooltip = ({selectedFilters = [], style={}}) => {
  const stopTooltipEvent = (event) => {
    event.stopPropagation();
  };

  return (
    <ol 
      className='cutom-filters-tooltip' 
      onMouseDown={stopTooltipEvent}
      onMouseUp={stopTooltipEvent}
      onClick={stopTooltipEvent}
      style={{
        ...style,
        listStyleType: selectedFilters.length === 1 ? 'none' : 'decimal',
        marginLeft: selectedFilters.length === 1 ? '0px' : '12px',
        userSelect: 'text',
        cursor: 'text',
      }}>
      {selectedFilters && selectedFilters.map((f, i)=>(
        <li key={i}>
          <Typography key={i} className='cutom-filters-tooltip-text' style={{ ...style, userSelect: 'text', cursor: 'text' }} data-testid={`filters-tooltip-${i}`}>
            {f}
          </Typography>
        </li>
      ))}
    </ol>
  )
}

const DEFAULT_CROSS_VIEW_TOOLTIP = 'This filter is from the other view';

const FilterBadge = memo((props) => {
  const {
    item, siblingFilter, onSwitchFilterMode, onRemove, onToggle, updateFilter, optionalProps = {}, disabled = false,
    disabledFromOtherViewTitle,
  } = props;
  const crossViewTooltip = disabledFromOtherViewTitle ?? DEFAULT_CROSS_VIEW_TOOLTIP;
  const isExclude = item?.mode === 'exclude' || (item?.id && String(item.id).endsWith('_exclude'));
  const metadata = JSON.parse(localStorage.getItem(METADATA_KEY) || '{}');
  const isNewCategoriesEnabled = Boolean(metadata?.ui_show_new_categories);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(item?.isOpen || false);
  const getFreetextDisplayValue = (filterItem) => {
    const checked = (filterItem?.filters || []).filter(f => f.checked);
    return checked.length > 0 ? checked[0].fid : '';
  };
  const [localValue, setLocalValue] = useState(item?.filterType === 'freetext' ? getFreetextDisplayValue(item) : (item?.value || ''));
  const [localSearchValue, setLocalSearchValue] = useState(item?.value || '');
  const [modeMenuAnchor, setModeMenuAnchor] = useState(null);
  const [modeMenuPosition, setModeMenuPosition] = useState(null);
  const [isStringInputFocused, setIsStringInputFocused] = useState(false);
  const activeFilterRef = useRef(null);
  const clickStartPos = useRef(null);
  const isDragging = useRef(false);
  const freetextEdited = useRef(false);
  const hasModeSwitch = Boolean(siblingFilter && (item?.filterType === 'array' || item?.filterType === 'select' || item?.filterType === 'string' || item?.filterType === 'freetext' || item?.filterType === 'date_picker'));

  const handleInputChange = (e) => {
    if (item?.filterType === 'freetext') freetextEdited.current = true;
    setLocalValue(e.target.value);
  };
  const handleStringInputClick = (e) => {
    e.stopPropagation();
  };
  const handleStringInputBlur = (e) => {
    setIsStringInputFocused(false);
    handleBlur(e);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur(e);
      e.target.blur();
      e.preventDefault();
    }
  };
  const handleBlur = (e) => {
    e.stopPropagation();
    if (item?.filterType === 'freetext') {
      if (!freetextEdited.current) return;
      freetextEdited.current = false;
      const trimmed = localValue.trim();
      const newFids = trimmed.length > 0 ? [trimmed] : [];
      const newFilters = newFids.map(fid => ({ fid, title: fid, checked: true }));
      if (item.localStorageKey) {
        localStorage.setItem(item.localStorageKey, JSON.stringify(newFids));
      }
      updateFilter(item.id, (curr) => ({
        ...curr,
        isActive: newFids.length > 0,
        filters: newFilters
      }));
      return;
    }
    if(localValue){
      if(item?.filterType !== "customBytes" || item?.filterType !== "conditional" ){ 
        localStorage.setItem(item.localStorageKey, JSON.stringify([localValue.trim()]));
        updateFilter(item.id, (item) => {
          item.value = localValue.trimEnd();
          return item;
        });
      } 
    } else {
      updateFilter(item.id, (item) => {
        item.isActive = false;
        return item;
      });
    }
  };
  
  const handleMouseDown = (e) => {
      clickStartPos.current = { x: e.clientX, y: e.clientY };
      isDragging.current = false;
  };

  const handleMouseMove = (e) => {
      if (clickStartPos.current) {
      const deltaX = Math.abs(e.clientX - clickStartPos.current.x);
      const deltaY = Math.abs(e.clientY - clickStartPos.current.y);
      if (deltaX > 5 || deltaY > 5) {
          isDragging.current = true;
      }
      }
  };

  const handleMouseUp = () => {
      clickStartPos.current = null;
      isDragging.current = false;
  };

  const handleSelectText = (e) => {
    if (isDragging.current) {
        return false;
    }

    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
        return false;
    }

    const isTextSelectable = (element) => {
        const style = window.getComputedStyle(element);
        return style.userSelect !== 'none' && style.cursor === 'text';
    };

    let target = e.target;
    while (target && target !== e.currentTarget) {
        if (isTextSelectable(target)) {
            return false;
        }
        target = target.parentElement;
    }
     if(e?.target?.className && (e?.target?.className?.includes('cutom-filters-tooltip-text') || e?.target?.className?.includes('cutom-filters-tooltip'))) {
      return false;
    }
    return true;
  }

  const filterTestIdKey = getFilterDataTestIdKey(item);

  useEffect(() => {
    if ((item?.filterType === 'string' || item?.filterType === 'freetext' || item?.filterType === 'customBytes' || item?.filterType === 'conditional') && item?.needsFocus && activeFilterRef?.current) {
      activeFilterRef.current.focus();
    }
  }, [item?.id]);

  useEffect(() => {
    if (item?.filterType === 'freetext') {
      setLocalValue(getFreetextDisplayValue(item));
      freetextEdited.current = false;
    } else {
      setLocalValue(item?.value || '');
    }
  }, [item?.value, item?.filters]);

  useEffect(() => {
    if(optionalProps && optionalProps?.searchValue){
      setLocalSearchValue(optionalProps?.searchValue);
    }
  }, [optionalProps]);
  if(item?.filterType === 'array') {
    const isCategoryFilter = (item?.baseId || item?.id) === 'categories';
    let checkedFilters = item.filters.filter(f => f.checked);
    checkedFilters = checkedFilters.map(f => {
      const isDeprecated = isNewCategoriesEnabled && isCategoryFilter && deprecatedCategoryList.some(cat => cat.id.toString() === f.fid.toString());
      return {
        ...f,
        title: isDeprecated ? `${f.title} (Deprecated)` : f.title
      }
    });
    const checkedFiltersString = checkedFilters.map(f => f.title).join(', ');
    const totalChars = checkedFiltersString.length;
    const maxChars = 33;
    const arrayStack = (
      <Stack className={`active-filter-btn${disabled ? ' active-filter-btn--disabled' : ''}`} aria-disabled={disabled}>
        {!disabled && <CloseRoundedIcon
          className="active-filter-icon"
          onClick={() => onRemove(item)}
          data-testid={`${filterTestIdKey}-remove`}
        />}
        <Typography
          className="active-filter-text"
          data-testid={`active-${filterTestIdKey}-filter`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={(e) => { if(handleSelectText(e)) onToggle(item); }}
        >
          {item?.localStorageKey === "browser_health_os_name" ? "OS Name" : sanitizeText(item?.title || "")}&nbsp;
          {hasModeSwitch ? (
            <>
              <span
                role="button"
                tabIndex={0}
                className="filter-mode-toggle"
                data-testid={`${filterTestIdKey}-mode-toggle`}
                style={{ color: isExclude ? '#FF6E6E' : '#1CC57E', cursor: 'pointer', textDecoration: 'none' }}
                onClick={(e) => { e.stopPropagation(); setModeMenuAnchor(e.currentTarget); setModeMenuPosition({ top: e.clientY, left: e.clientX }); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setModeMenuAnchor(e.currentTarget); const r = e.currentTarget.getBoundingClientRect(); setModeMenuPosition({ top: r.bottom, left: r.left }); } }}
              >
                {isExclude ? 'Exclude' : 'Include'}
              </span>
              <Menu
                anchorReference="anchorPosition"
                anchorPosition={modeMenuPosition}
                open={Boolean(modeMenuAnchor)}
                onClose={() => { setModeMenuAnchor(null); setModeMenuPosition(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClick={(e) => e.stopPropagation()}
                MenuListProps={{ 'data-testid': `filter-mode-menu-${filterTestIdKey}` }}
                className='filter-mode-menu'
              >
                <MenuItem
                  onClick={() => { if (!isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#1CC57E', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-include`}
                >
                  <AddCircleOutlineIcon style={{ color: '#1CC57E', fontSize: '14px' }} /> Include
                </MenuItem>
                <MenuItem
                  onClick={() => { if (isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#E63D32', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-exclude`}
                >
                  <RemoveCircleOutlineIcon style={{ color: '#E63D32', fontSize: '14px' }} />
                  Exclude
                </MenuItem>
              </Menu>
            </>
          ) : (
            <span style={{ color: isExclude ? '#E63D32' : '#1CC57E' }}>{isExclude ? 'Exclude' : 'Include'}</span>
          )}
          &nbsp;|&nbsp;
          {disabled ? (
            <span className="selected-filters" data-testid={`active-${filterTestIdKey}-text`}>
              {totalChars < maxChars ? checkedFiltersString : `${`${checkedFilters[0]?.title}`.slice(0, maxChars)}...${checkedFilters.length > 1 ? `and ${checkedFilters.length - 1} more` : ''}`}
            </span>
          ) : (
            <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={checkedFilters.map(f => f.title)} />}>
              <span className="selected-filters" data-testid={`active-${filterTestIdKey}-text`}>
                {totalChars < maxChars ? checkedFiltersString : `${`${checkedFilters[0]?.title}`.slice(0, maxChars)}...${checkedFilters.length > 1 ? `and ${checkedFilters.length - 1} more` : ''}`}
              </span>
            </NoMaxWidthTooltip>
          )}
        </Typography>
      </Stack>
    );
    return disabled ? (
      <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
        {arrayStack}
      </NoMaxWidthTooltip>
    ) : (
      arrayStack
    );
  }
  if(item?.filterType === 'freetext') {
    const freetextStack = (
      <Stack className={`active-filter-btn${disabled ? ' active-filter-btn--disabled' : ''}`} aria-disabled={disabled}>
        {!disabled && <CloseRoundedIcon
          className="active-filter-icon"
          onClick={() => onRemove(item)}
          data-testid={`${filterTestIdKey}-remove`}
        />}
        <Typography
          className="active-filter-text"
          onClick={() => {}}
          data-testid={`active-${filterTestIdKey}-filter`}
        >
          {sanitizeText(item?.title || "")}&nbsp;
          {hasModeSwitch ? (
            <>
              <span
                role="button"
                tabIndex={0}
                className="filter-mode-toggle"
                data-testid={`${filterTestIdKey}-mode-toggle`}
                style={{ color: isExclude ? '#FF6E6E' : '#1CC57E', cursor: 'pointer', textDecoration: 'none' }}
                onClick={(e) => { e.stopPropagation(); setModeMenuAnchor(e.currentTarget); setModeMenuPosition({ top: e.clientY, left: e.clientX }); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setModeMenuAnchor(e.currentTarget); const r = e.currentTarget.getBoundingClientRect(); setModeMenuPosition({ top: r.bottom, left: r.left }); } }}
              >
                {isExclude ? 'Exclude' : 'Include'}
              </span>
              <Menu
                anchorReference="anchorPosition"
                anchorPosition={modeMenuPosition}
                open={Boolean(modeMenuAnchor)}
                onClose={() => { setModeMenuAnchor(null); setModeMenuPosition(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClick={(e) => e.stopPropagation()}
                MenuListProps={{ 'data-testid': `filter-mode-menu-${filterTestIdKey}` }}
                className='filter-mode-menu'
              >
                <MenuItem
                  onClick={() => { if (!isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#1CC57E', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-include`}
                >
                  <AddCircleOutlineIcon style={{ color: '#1CC57E', fontSize: '14px' }} /> Include
                </MenuItem>
                <MenuItem
                  onClick={() => { if (isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#E63D32', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-exclude`}
                >
                  <RemoveCircleOutlineIcon style={{ color: '#E63D32', fontSize: '14px' }} />
                  Exclude
                </MenuItem>
              </Menu>
            </>
          ) : (
            <span style={{ color: isExclude ? '#E63D32' : '#1CC57E' }}>{isExclude ? 'Exclude' : 'Include'}</span>
          )}
          &nbsp;{localValue && '|'}&nbsp;
        </Typography>
        {disabled ? (
          <span className="selected-filters">{localValue}</span>
        ) : (
          <NoMaxWidthTooltip
            disableFocusListener
            title={!isStringInputFocused && localValue ? <CustomTooltip selectedFilters={[localValue]} /> : ""}
          >
            <Input
              className='custom-filter-text-input'
              inputRef={activeFilterRef}
              value={localValue}
              onClick={handleStringInputClick}
              onFocus={() => setIsStringInputFocused(true)}
              onChange={handleInputChange}
              onBlur={handleStringInputBlur}
              data-testid={`active-${filterTestIdKey}-input`}
              inputProps={{
                "data-testid" : `active-${filterTestIdKey}-inputbox`
              }}
              onKeyDown={handleKeyDown}
            />
          </NoMaxWidthTooltip>
        )}
      </Stack>
    );
    return disabled ? (
      <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
        {freetextStack}
      </NoMaxWidthTooltip>
    ) : (
      freetextStack
    );
  }
  if(item?.filterType === 'select') {
    const checkedFilters = item.filters.filter(f => f.checked);
    const selectStack = (
      <Stack className={`active-filter-btn${disabled ? ' active-filter-btn--disabled' : ''}`} aria-disabled={disabled}>
        {!disabled && <CloseRoundedIcon
          className="active-filter-icon"
          onClick={() => onRemove(item)}
          data-testid={`${filterTestIdKey}-remove`}
        />}
        <Typography
          className="active-filter-text"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={(e) => { if(handleSelectText(e)) onToggle(item); }}
          data-testid={`active-${filterTestIdKey}-filter`}
        >
          {sanitizeText(item?.title || "")}&nbsp;
          {hasModeSwitch ? (
            <>
              <span
                role="button"
                tabIndex={0}
                className="filter-mode-toggle"
                data-testid={`${filterTestIdKey}-mode-toggle`}
                style={{ color: isExclude ? '#E63D32' : '#1CC57E', cursor: 'pointer', textDecoration: 'none' }}
                onClick={(e) => { e.stopPropagation(); setModeMenuAnchor(e.currentTarget); setModeMenuPosition({ top: e.clientY, left: e.clientX }); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setModeMenuAnchor(e.currentTarget); const r = e.currentTarget.getBoundingClientRect(); setModeMenuPosition({ top: r.bottom, left: r.left }); } }}
              >
                {isExclude ? 'Exclude' : 'Include'}
              </span>
              <Menu
                anchorReference="anchorPosition"
                anchorPosition={modeMenuPosition}
                open={Boolean(modeMenuAnchor)}
                onClose={() => { setModeMenuAnchor(null); setModeMenuPosition(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClick={(e) => e.stopPropagation()}
                MenuListProps={{ 'data-testid': `filter-mode-menu-${filterTestIdKey}` }}
                className='filter-mode-menu'
              >
                <MenuItem
                  onClick={() => { if (!isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#1CC57E', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-include`}
                >
                  <AddCircleOutlineIcon style={{ color: '#1CC57E', fontSize: '14px' }} /> Include
                </MenuItem>
                <MenuItem
                  onClick={() => { if (isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#FF6E6E', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-exclude`}
                >
                  <RemoveCircleOutlineIcon style={{ color: '#E63D32', fontSize: '14px' }} />
                  Exclude
                </MenuItem>
              </Menu>
            </>
          ) : (
            <span style={{ color: isExclude ? '#E63D32' : '#1CC57E' }}>{isExclude ? 'Exclude' : 'Include'}</span>
          )}
          &nbsp;|&nbsp;
          {disabled ? (
            <span className="selected-filters" data-testid={`active-${filterTestIdKey}-text`}>
              {checkedFilters?.map(f => f.title).join(', ')}
            </span>
          ) : (
            <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={checkedFilters.map(f => f.title)} />}>
              <span className="selected-filters" data-testid={`active-${filterTestIdKey}-text`}>
                {checkedFilters?.map(f => f.title).join(', ')}
              </span>
            </NoMaxWidthTooltip>
          )}
        </Typography>
      </Stack>
    );
    return disabled ? (
      <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
        {selectStack}
      </NoMaxWidthTooltip>
    ) : (
      selectStack
    );
  }
  if(item?.filterType === 'date') {      
    return (
      <Stack className="active-filter-btn">
        <CloseRoundedIcon
          className="active-filter-icon"
          onClick={() => {
            onRemove(item);
          }}
          data-testid={`${filterTestIdKey}-remove`}
        />
        <Typography
          className="active-filter-text"
          onClick={() => {}}
          data-testid={`active-${filterTestIdKey}-filter`}
        >
          {sanitizeText(item?.title || "")}&nbsp;
          {item?.value && '|'}&nbsp;
        </Typography>
        
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={new Date(item?.value)}
            minDate={new Date(JSON.parse(localStorage.getItem(METADATA_KEY)).genesis)}
            maxDate={new Date()}
            onChange={(newValue) => {
              // set in localstorage of the filter localstorage key
              const formattedDate = new Date(newValue).toUTCString();
              localStorage.setItem(item.localStorageKey, JSON.stringify([formattedDate]));
              updateFilter(item.id, (item) => {(item.value = newValue.toISOString()); return item;});
              setIsDatePickerOpen(false);
            }}
            hiddenLabel
            open={isDatePickerOpen}
            onClose={() => setIsDatePickerOpen(false)}
            PopperProps={{
              disablePortal: true,
            }}
            renderInput={(params) => {
              return (
                <div 
                  className="custom-filter-date-picker" 
                  onClick={() => {
                    setIsDatePickerOpen(p=>!p);
                  }}
                >
                  <CssTextField 
                    style={{width: '100%', marginTop: 'auto'}}
                    data-testid={`active-${filterTestIdKey}-dateinput`}
                    hiddenLabel
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: null
                    }}
                    inputProps={{ 
                      ...params.inputProps, 
                      readOnly: true,
                      onClick: (e) => {
                        e.stopPropagation();
                        setIsDatePickerOpen(p=>!p);
                        onToggle(item);
                      }
                    }}
                  />
                </div>
              );
            }}
          />
        </LocalizationProvider>
      </Stack>
    );
  }
  if(item?.filterType === 'date_picker') {
    const datePickerStack = (
      <Stack className={`active-filter-btn${disabled ? ' active-filter-btn--disabled' : ''}`} aria-disabled={disabled}>
        {!disabled && <CloseRoundedIcon
          className="active-filter-icon"
          onClick={() => {
            onRemove(item);
          }}
          data-testid={`${filterTestIdKey}-remove`}
        />}
        <Stack direction={'row'} gap={1}
          onMouseDown={disabled ? undefined : handleMouseDown}
          onMouseMove={disabled ? undefined : handleMouseMove}
          onMouseUp={disabled ? undefined : handleMouseUp}
          onClick={disabled ? undefined : (e)=>{
            if(handleSelectText(e)) onToggle(item);
            }}>
          <Typography
            className="active-filter-text"
            onClick={() => {}}
            data-testid={`active-${filterTestIdKey}-filter`}
          >
            {sanitizeText(item?.title || "")}&nbsp;
            {!disabled && hasModeSwitch ? (
              <>
                <span
                  role="button"
                  tabIndex={0}
                  className="filter-mode-toggle"
                  style={{ color: isExclude ? '#FF6E6E' : '#1CC57E', cursor: 'pointer', textDecoration: 'none' }}
                  onClick={(e) => { e.stopPropagation(); setModeMenuAnchor(e.currentTarget); setModeMenuPosition({ top: e.clientY, left: e.clientX }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setModeMenuAnchor(e.currentTarget); const r = e.currentTarget.getBoundingClientRect(); setModeMenuPosition({ top: r.bottom, left: r.left }); } }}
                >
                  {isExclude ? 'Exclude' : 'Include'}
                </span>
                <Menu
                  anchorReference="anchorPosition"
                  anchorPosition={modeMenuPosition}
                  open={Boolean(modeMenuAnchor)}
                  onClose={() => { setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  onClick={(e) => e.stopPropagation()}
                  MenuListProps={{ 'data-testid': `filter-mode-menu-${filterTestIdKey}` }}
                  className='filter-mode-menu'
                >
                  <MenuItem
                    onClick={() => { if (!isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                    style={{ color: '#1CC57E', display: 'flex', alignItems: 'center', gap: "4px" }}
                    data-testid={`${filterTestIdKey}-mode-include`}
                  >
                    <AddCircleOutlineIcon style={{ color: '#1CC57E', fontSize: '14px' }} /> Include
                  </MenuItem>
                  <MenuItem
                    onClick={() => { if (isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                    style={{ color: '#E63D32', display: 'flex', alignItems: 'center', gap: "4px" }}
                    data-testid={`${filterTestIdKey}-mode-exclude`}
                  >
                    <RemoveCircleOutlineIcon style={{ color: '#E63D32', fontSize: '14px' }} />
                    Exclude
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            &nbsp;{item?.value && '|'}&nbsp;
          </Typography>
          <span className="selected-filters" data-testid={`active-${filterTestIdKey}-text`}>
            {item?.value?.quickLabel &&
            QUICK_DATE_RANGE_OPTIONS.some((opt) => opt.label === item.value.quickLabel)
              ? item.value.quickLabel
              : `${convertDate(item?.value?.start_date)} To ${convertDate(item?.value?.end_date)}`}
          </span>
        </Stack>
      </Stack>
    );
    return disabled ? (
      <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
        {datePickerStack}
      </NoMaxWidthTooltip>
    ) : (
      datePickerStack
    );
  }
  if(item?.filterType === 'string') {
    const stringStack = (
      <Stack className={`active-filter-btn${disabled ? ' active-filter-btn--disabled' : ''}`} aria-disabled={disabled}>
        {!disabled && <CloseRoundedIcon
          className="active-filter-icon"
          onClick={() => onRemove(item)}
          data-testid={`${filterTestIdKey}-remove`}
        />}
        <Typography
          className="active-filter-text"
          onClick={() => {}}
          data-testid={`active-${filterTestIdKey}-filter`}
        >
          {sanitizeText(item?.title || "")}&nbsp;
          {hasModeSwitch ? (
            <>
              <span
                role="button"
                tabIndex={0}
                className="filter-mode-toggle"
                style={{ color: isExclude ? '#FF6E6E' : '#1CC57E', cursor: 'pointer', textDecoration: 'none' }}
                onClick={(e) => { e.stopPropagation(); setModeMenuAnchor(e.currentTarget); setModeMenuPosition({ top: e.clientY, left: e.clientX }); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setModeMenuAnchor(e.currentTarget); const r = e.currentTarget.getBoundingClientRect(); setModeMenuPosition({ top: r.bottom, left: r.left }); } }}
              >
                {isExclude ? 'Exclude' : 'Include'}
              </span>
              <Menu
                anchorReference="anchorPosition"
                anchorPosition={modeMenuPosition}
                open={Boolean(modeMenuAnchor)}
                onClose={() => { setModeMenuAnchor(null); setModeMenuPosition(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClick={(e) => e.stopPropagation()}
                MenuListProps={{ 'data-testid': `filter-mode-menu-${filterTestIdKey}` }}
                className='filter-mode-menu'
              >
                <MenuItem
                  onClick={() => { if (!isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#1CC57E', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-include`}
                >
                  <AddCircleOutlineIcon style={{ color: '#1CC57E', fontSize: '14px' }} /> Include
                </MenuItem>
                <MenuItem
                  onClick={() => { if (isExclude) return; onSwitchFilterMode?.(item, siblingFilter); setModeMenuAnchor(null); setModeMenuPosition(null); }}
                  style={{ color: '#E63D32', display: 'flex', alignItems: 'center', gap: "4px" }}
                  data-testid={`${filterTestIdKey}-mode-exclude`}
                >
                  <RemoveCircleOutlineIcon style={{ color: '#E63D32', fontSize: '14px' }} />
                  Exclude
                </MenuItem>
              </Menu>
            </>
          ) : item?.baseId ? (
            <span style={{ color: isExclude ? '#E63D32' : '#1CC57E' }}>{isExclude ? 'Exclude' : 'Include'}</span>
          ) : null}
          &nbsp;{localValue && '|'}&nbsp;
        </Typography>
        {disabled ? (
          <span className="selected-filters">{localValue}</span>
        ) : (
          <NoMaxWidthTooltip
            disableFocusListener
            title={!isStringInputFocused && localValue ? <CustomTooltip selectedFilters={[localValue]} /> : ""}
          >
            <Input
              className='custom-filter-text-input'
              inputRef={activeFilterRef}
              value={localValue}
              onClick={handleStringInputClick}
              onFocus={() => setIsStringInputFocused(true)}
              onChange={handleInputChange}
              onBlur={handleStringInputBlur}
              data-testid={`active-${filterTestIdKey}-input`}
              inputProps={{
                "data-testid" : `active-${filterTestIdKey}-inputbox`
              }}
              onKeyDown={handleKeyDown}
            />
          </NoMaxWidthTooltip>
        )}
      </Stack>
    );
    return disabled ? (
      <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
        {stringStack}
      </NoMaxWidthTooltip>
    ) : (
      stringStack
    );
  }
  if(item?.filterType === 'customBytes') {
    if (disabled) {
      return (
        <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
          <Stack className="active-filter-btn active-filter-btn--disabled" aria-disabled="true">
            <Typography className="active-filter-text">
              {sanitizeText(item?.title || "")}&nbsp;|&nbsp;
            </Typography>
            <span className="selected-filters">{localValue?.value || ''}</span>
          </Stack>
        </NoMaxWidthTooltip>
      );
    }
    return (
      <NoMaxWidthTooltip title={localValue ? <CustomTooltip selectedFilters={[localValue?.value]} /> : ""}>
        <Stack className="active-filter-btn">
            <CloseRoundedIcon
              className="active-filter-icon"
              onClick={() => onRemove(item)}
              data-testid={`${filterTestIdKey}-remove`}
            />
          <CustomBytesFilter
            value={localValue}
            title={`${item?.title}`}
            inputRef={activeFilterRef}
            localStorageKey={item?.localStorageKey}
            dataTestIdKey={filterTestIdKey}
            onRemove={() => onRemove(item)}
            setLocalInputValue={(value)=>{
              if(value && value?.trim()){
                setLocalValue(p=>({...p, value}));
              }
            }}
            onChange={(value) => {
              if(value?.value) {
                setLocalValue(value);
                localStorage.setItem(item.localStorageKey, JSON.stringify([{value: value?.value, condition: value?.condition, size: value?.size}]));
                updateFilter(item.id, (item) => {
                    item.value = value;
                    return item;
                  });
              }
            }}
            onClick={() => onToggle(item)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            item={item}
            updateFilter={updateFilter}
          />
        </Stack>
      </NoMaxWidthTooltip>
    )
  }
  if(item?.filterType === 'conditional') {
    if (disabled) {
      return (
        <NoMaxWidthTooltip title={<CustomTooltip selectedFilters={[crossViewTooltip]} />} placement="top">
          <Stack className="active-filter-btn active-filter-btn--disabled" aria-disabled="true">
            <Typography className="active-filter-text">
              {sanitizeText(item?.title || "")}&nbsp;|&nbsp;
            </Typography>
            <span className="selected-filters">{localValue?.value || ''}</span>
          </Stack>
        </NoMaxWidthTooltip>
      );
    }
    return (
      <NoMaxWidthTooltip title={localValue ? <CustomTooltip selectedFilters={[localValue?.value]} /> : ""}>
        <Stack className="active-filter-btn">
            <CloseRoundedIcon
              className="active-filter-icon"
              onClick={() => onRemove(item)}
              data-testid={`${filterTestIdKey}-remove`}
            />
          <CustomConditionalFilter
            value={localValue}
            title={`${item?.title}`}
            inputRef={activeFilterRef}
            localStorageKey={item?.localStorageKey}
            dataTestIdKey={filterTestIdKey}
            onRemove={() => onRemove(item)}
            setLocalInputValue={(value)=>{
              if(value && value?.trim()){
                setLocalValue(p=>({...p, value}));
              }
            }}
            onChange={(value) => {
              if(value?.value) {
                setLocalValue(value);
                localStorage.setItem(item.localStorageKey, JSON.stringify([{value: value?.value, condition: value?.condition, size: value?.size}]));
                updateFilter(item.id, (item) => {
                    item.value = value;
                    return item;
                  });
              }
            }}
            onClick={() => onToggle(item)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            item={item}
            updateFilter={updateFilter}
          />
        </Stack>
      </NoMaxWidthTooltip>
    )
  }
  if(item?.id === 'saasV2_search') {
    return (
      <NoMaxWidthTooltip title={localSearchValue ? <CustomTooltip selectedFilters={[localSearchValue]} /> : ""}>
        <Stack className="active-filter-btn">
          <CloseRoundedIcon
            className="active-filter-icon"
            onClick={() => onRemove(item)}
            data-testid={`${filterTestIdKey}-remove`}
          />
          <Typography
            className="active-filter-text"
            onClick={() => {}}
            data-testid={`active-${filterTestIdKey}-filter`}
          >
            {sanitizeText(item?.title || "")}&nbsp;
            {localValue && '|'}&nbsp;
          </Typography>
          <Input
            className='custom-filter-text-input'
            inputRef={activeFilterRef} 
            value={localSearchValue}
            onClick={() => onToggle(item)}
            onChange={(e)=>{
              setLocalSearchValue(e.target.value);
              optionalProps?.handleSearchValueChange(e.target.value);
            }}
            onBlur={(e)=>{
               e.stopPropagation();
               if(!localSearchValue){
                 updateFilter(item.id, (item) => {
                    item.isActive = false;
                    return item;
                  });
                }
            }}
            data-testid={`active-${filterTestIdKey}-input`}
            autoFocus={item?.filterType === 'saasV2_search'}
            // onKeyDown={handleKeyDown}
          />
        </Stack>
      </NoMaxWidthTooltip>
    );
  }
  return null;
});

export default FilterBadge;